// Copyright (c) Move Closer 2022.

import { Component, Mixins } from 'vue-property-decorator'
import { AnyObject, EventbusType, EventPayload, IEventbus } from '@movecloser/front-core'

import { AllowedAttributes, CartItemData, ProductData } from '../../../contexts'
import { Inject, logger } from '../../../support'

import { CartRestoredPayload } from '../../checkout/views/cart/Cart.config'
import { CART_ID_KEY, CartServiceType, ICartService } from '../../checkout/services/cart'
import { HandleActionOptions } from '../../checkout/molecules/CartItem/CartItem.contracts'
import { IProductsRepository, ProductsRepositoryType } from '../../products/contracts/repositories'
import { ProductCartMixin } from '../../checkout/shared/mixins/product-cart.mixin'

import { CandidateGift, CandidateSample, ResolvedCandidateGift, UpdatedQuantityEventPayload } from '../contracts/gifts'
import { CartViewMixin } from './cartView.mixin'
import { GiftAbstract } from '../abstract/gift-abstract'

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 *
 * Mixin capable of handling actions on cart gifts and samples
 */
@Component<CartGiftMixin>({
  name: 'CartGiftMixin',
  mounted () {
    this.handleEvents()
  },
  destroyed () {
    this.cartItemRestored = false
  }
})
export class CartGiftMixin extends Mixins(GiftAbstract, CartViewMixin, ProductCartMixin) {
  @Inject(CartServiceType)
  public readonly cartService!: ICartService

  @Inject(EventbusType)
  protected readonly eventBus!: IEventbus

  @Inject(ProductsRepositoryType)
  protected readonly productsRepository!: IProductsRepository

  public gifts: Array<ProductData> | null = null
  public samples: Array<ProductData> | null = null
  public cartItemRestored: boolean = false

  protected async addItemToCart (product: Array<AnyObject>) {
    await this.addToCart(product[0], 1, false)
  }

  /**
   * Loads product data for samples products
   * @protected
   */
  protected async loadSamples (cartItems: Array<CartItemData>): Promise<void> {
    /**
     * @inheritDoc
     */
    this.checkIfUserCanPick(this.cart, AllowedAttributes.IsSample)

    if (!this.canPickSamples) {
      return
    }

    /** Collection of samples skus from cart items */
    const samples: Array<CandidateSample> = this.translateToSkus(cartItems, AllowedAttributes.SamplesSku)

    try {
      if (samples.length > 0) {
        const resolvedSamples = await this.productsRepository.loadProductsBySkus(samples.map((item) => item.sku))

        if (resolvedSamples && resolvedSamples.length > 0) {
          this.samples = this.getAvailableProductsOnly(resolvedSamples)
        }
      }
    } catch (e) {
      logger(e, 'warn')
    }
  }

  /**
   * 1. Loads product data for gifts products
   * 2. Add all gifts to the current cart immediately (required business logic)
   * @protected
   */
  protected async loadGifts (cartItems: Array<CartItemData>, uid: string | null = null, hasBeenRestored: boolean = false): Promise<void> {
    if (!this.canPickGifts || !this.cart) {
      return
    }

    const gifts = this.retrieveGiftsFromProduct(cartItems, uid, hasBeenRestored)

    try {
      if (gifts.length > 0) {
        const resolvedGifts = await this.productsRepository.loadProductsBySkus(gifts.map((item) => item.sku))

        if (resolvedGifts && resolvedGifts.length > 0) {
          this.gifts = this.getAvailableProductsOnly(resolvedGifts)

          await this.handleGiftsLoad(cartItems, gifts, this.gifts, hasBeenRestored)
        }
      }
    } catch (e) {
      logger(e, 'warn')
    }
  }

  /**
   * Main function capable of loading gifts.
   * It takes into account some conditions:
   * --- whether cart should load gifts FOR THE FIRST TIME with quantity corresponding a quantity of product that produces gifts ('add item to cart' action)
   * --- whether cart should load gifts NOT FOR THE FIRST TIME with proper quantity ('update item' action)
   * --- whether gifts have been restored accordingly to the restored product that produces gifts
   *
   * @param cartItems
   * @param candidateGifts - collection of gift skus with proper quantity
   * @param resolvedGifts - collection of gifts with resolved product data
   * @param hasBeenRestored - indicator whether a gift must be restored from previous quantity '0'
   * @protected
   */
  protected async handleGiftsLoad (
    cartItems: Array<CartItemData>,
    candidateGifts: Array<CandidateGift>,
    resolvedGifts: Array<ProductData>,
    hasBeenRestored: boolean
  ): Promise<void> {
    /** Collection of items that have to be ADDED to the cart */
    let finalCandidates: Array<ResolvedCandidateGift> = []

    if (!hasBeenRestored) {
      /** Collection of items that should be UPDATED instead of added to cart */
      const itemsToUpdate: any = []
      const temp = []

      for (const item of cartItems) {
        if (item.isGift) {
          for (const gift of candidateGifts) {
            const variantSku = Object.values(item.product.variants)[0].sku
            if (gift.sku === variantSku) {
              itemsToUpdate.push({
                uid: item.uid,
                quantity: gift.quantity,
                sku: variantSku
              })
            }
          }
        } else {
          for (const variant of Object.values(item.product.variants)) {
            if (variant.attributes[AllowedAttributes.GiftsSku] && (variant.attributes[AllowedAttributes.GiftsSku] as string[]).length > 0) {
              temp.push({
                giftSkus: variant.attributes[AllowedAttributes.GiftsSku],
                quantity: item.quantity
              })
            }
          }
        }
      }

      for (const gift of resolvedGifts) {
        for (const variant of Object.values(gift.variants)) {
          if (variant.attributes[AllowedAttributes.IsGift]) {
            for (const item of temp) {
              if ((item.giftSkus as string[]).includes(variant.sku)) {
                finalCandidates.push({
                  product: variant,
                  requestedQuantity: item.quantity
                })
              }
            }
          }
        }
      }

      finalCandidates = finalCandidates
        .filter((candidate) => ![...itemsToUpdate.map((item: any) => item.sku)]
          .includes(candidate.product.sku))

      if (itemsToUpdate.length > 0) {
        try {
          const updatedCart = await this.cartService.updateCartItems(
            this.cartId,
            itemsToUpdate
              .map((item: any) => {
                return {
                  uid: item.uid,
                  quantity: item.quantity
                }
              })
          )

          this.refreshCart(updatedCart)
        } catch (e) {
          logger(e, 'warn')
        }
      }

      if (finalCandidates.length > 0) {
        await this.addGiftsToCart(finalCandidates)
      }
    } else {
      if (resolvedGifts.length > 0) {
        for (const gift of resolvedGifts) {
          for (const variant of Object.values(gift.variants)) {
            if (variant.attributes[AllowedAttributes.IsGift]) {
              for (const item of candidateGifts) {
                if (item.sku === variant.sku) {
                  finalCandidates.push({
                    product: variant,
                    requestedQuantity: item.quantity
                  })
                }
              }
            }
          }
        }
      }

      if (finalCandidates.length > 0) {
        await this.addGiftsToCart(finalCandidates)
      }
    }
  }

  /**
   * Updated gift quantity when a quantity of parent element has changed (parent element is a product that produces a gift)
   * @param updatedQuantity
   * @protected
   */
  protected async updateGiftsQuantity (updatedQuantity: UpdatedQuantityEventPayload) {
    if (!this.cart || !updatedQuantity.valid) {
      return
    }

    /** Regular cart items which quantity has been updated */
    const updatedCartItem = this.cart.items.filter((item) => item.uid === updatedQuantity.uid)
    /** Current cart items that are gifts */
    const giftCartItems = this.cart.items.filter((item) => item.isGift)
    /** Candidate skus from parent item */
    const candidateSkus = this.translateToSkus(updatedCartItem, AllowedAttributes.GiftsSku)

    const candidates = []
    for (const gift of giftCartItems) {
      for (const candidate of candidateSkus) {
        if (Object.values(gift.product.variants)[0].sku === candidate.sku) {
          if (gift.quantity !== updatedQuantity.newQuantity) {
            candidates.push({
              uid: gift.uid,
              quantity: updatedQuantity.newQuantity
            })
          }
        }
      }
    }

    try {
      const updatedCart = await this.cartService.updateCartItems(this.cartId, candidates)
      this.refreshCart(updatedCart)
    } catch (e) {
      logger(e, 'warn')
    }
  }

  /**
   * Updates available gifts when amount of single cartItem reaches '0' level (when user deleted the item)
   * @protected
   */
  protected async updateItems (options: HandleActionOptions): Promise<void> {
    /**
     * @inheritDoc
     */
    await this.checkIfItemsShouldBeDeleted(options, AllowedAttributes.IsSample, AllowedAttributes.SamplesSku)

    if (!this.cart) {
      return
    }

    const deletedCartItems = this.cart?.items.filter((item) => item.uid === options.uid)
    const deletedSamples = this.translateToSkus(deletedCartItems, AllowedAttributes.SamplesSku)

    setTimeout(() => {
      if (this.samples) {
        this.samples = this.samples.filter((sample) => !deletedSamples.map((item) => item.sku).includes(sample.sku as string))
      }
    }, 500)

    this.checkIfUserCanPick(this.cart, AllowedAttributes.SamplesSku)
  }

  /**
   * Adds all available gifts to cart
   * @param gifts
   * @private
   */
  private async addGiftsToCart (gifts: Array<ResolvedCandidateGift>) {
    this.eventBus.emit('cart:gifts-loader-emit', true)
    try {
      for (const gift of gifts) {
        await this.addToCart(gift.product, gift.requestedQuantity, false)
      }
    } catch (e) {
      logger(e, 'warn')
    } finally {
      this.eventBus.emit('cart:gifts-loader-emit', false)
    }
  }

  /**
   * It checks if samples or gifts should be deleted from cart e.g. by deleting other regular cart item
   * @private
   */
  private async checkIfItemsShouldBeDeleted (options: HandleActionOptions, type: string, collectionType: string): Promise<void> {
    if (!this.cart) {
      return
    }

    const removedItem = this.cart.items.filter((item) => item.uid === options.uid)
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const currentExtraItems = this.cart.items.filter((item) => item[type])

    let itemToDelete = null

    for (let i = 0; i < removedItem.length; i++) {
      for (let j = 0; j < currentExtraItems.length; j++) {
        const itemSku = Object.values(removedItem[i].product.variants)[0].attributes[collectionType] as string[]
        if (itemSku && itemSku.includes(currentExtraItems[j].product.sku as string)) {
          itemToDelete = this.cart?.items.find((item) => item.product.sku === currentExtraItems[j].product.sku)
          break
        }
      }
    }

    const cartId = this.cartService.getCartId()
    if (!cartId || !itemToDelete) {
      return
    }

    try {
      await this.removeFromCart(itemToDelete.uid)
    } catch (e) {
      logger(e, 'warn')
    }
  }

  private handleEvents (): void {
    /**
     * Event applicable only for case when user deletes regular cart item
     */
    this.eventBus.handle('cart:item-deleted', async (event: EventPayload<HandleActionOptions>) => {
      if (event.payload) {
        /**
         * @inheritDoc
         */
        await this.updateItems(event.payload)
      }
    })

    /**
     * Event that handles situation when cart item has been restored from quantity '0'
     */
    this.eventBus.handle('app:cart.gift-item-valid-quantity', async (event: EventPayload<CartRestoredPayload>) => {
      if (event.payload && event.payload.gifts.length > 0 && this.cart) {
        this.cartItemRestored = true
        await this.loadGifts(this.cart.items, event.payload.uid, true)
      }
    })

    /**
     * Event that handles update of gifts quantity
     */
    this.eventBus.handle('app:cart.updated-item-quantity', async (event: EventPayload<UpdatedQuantityEventPayload>) => {
      if (event.payload) {
        await this.updateGiftsQuantity(event.payload)
      }
    })
  }
}
